import {ElementRef, Injectable} from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsService {

  private loading: Promise<boolean>;
  /** False only while the Maps script is being fetched; consumers hold their fallback UI until then. */
  public settled = false;

  constructor() {

  }

  /**
   * Injects the Maps script once, on first use, so screens without a map, address search or drawing
   * never contact Google (#1755). Resolves true when google.maps is usable; never rejects.
   * The key comes from the `google-maps-key` meta tag in frontend.blade.php; without one (specs)
   * nothing is injected.
   */
  public load(): Promise<boolean> {
    const w = window as any;
    if (w.google?.maps || w.__gmAuthFailed) {
      this.settled = true;
      return Promise.resolve(!!w.google?.maps);
    }
    const key = document.querySelector('meta[name="google-maps-key"]')?.getAttribute('content');
    if (!key) {
      this.settled = true;
      return Promise.resolve(false);
    }
    return this.loading ??= new Promise<boolean>(resolve => {
      const done = (ok: boolean) => { this.settled = true; resolve(ok); };
      // Google calls this on a rejected key, after the script has loaded, and may never call initMap.
      w.gm_authFailure = () => {
        w.__gmAuthFailed = true;
        window.dispatchEvent(new Event('gm-auth-failure'));
        done(false);
      };
      w.initMap = () => done(true);
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=drawing,places&callback=initMap`;
      script.async = true;
      // A failed fetch is retried on the next load() instead of being cached.
      script.onerror = () => { this.loading = null; script.remove(); done(false); };
      document.head.appendChild(script);
    });
  }

  public async newMap(attach: ElementRef, options: google.maps.MapOptions){
    return new google.maps.Map(attach.nativeElement, options);
  }

}
